import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  GetMyMemberAboutResponse,
  MemberAbout,
  MemberAboutCreatedEnvelope,
  MemberAboutDeletedEnvelope,
  MemberAboutQuery,
  MemberAboutUpdatedEnvelope,
  MemberAboutsQueryBuilder,
  QueryMemberAboutsResponse,
  UpdateMemberAbout,
  createMemberAbout as universalCreateMemberAbout,
  deleteMemberAbout as universalDeleteMemberAbout,
  getMemberAbout as universalGetMemberAbout,
  getMyMemberAbout as universalGetMyMemberAbout,
  queryMemberAbouts as universalQueryMemberAbouts,
  typedQueryMemberAbouts as universalTypedQueryMemberAbouts,
  updateMemberAbout as universalUpdateMemberAbout,
} from './members-about-v2-member-about-members-about.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/members' };

export function createMemberAbout(
  httpClient: HttpClient
): CreateMemberAboutSignature {
  return (memberAbout: NonNullablePaths<MemberAbout, `content`, 2>) =>
    universalCreateMemberAbout(
      memberAbout,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateMemberAboutSignature {
  /**
   * Creates the "About" section content for a member.
   * @param - The "About" section content to create.
   * @returns The created "About" section content.
   */
  (
    memberAbout: NonNullablePaths<MemberAbout, `content`, 2>
  ): Promise<MemberAbout>;
}

export function updateMemberAbout(
  httpClient: HttpClient
): UpdateMemberAboutSignature {
  return (
    _id: string,
    memberAbout: NonNullablePaths<UpdateMemberAbout, `content` | `revision`, 2>
  ) =>
    universalUpdateMemberAbout(
      _id,
      memberAbout,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateMemberAboutSignature {
  /**
   * Updates the "About" section content for a member.
   *
   * Each time the "About" section content is updated, `revision` increments by 1. The existing `revision` must be included when updating the "About" section content. This ensures you're working with the latest "About" section and prevents unintended overwrites.
   * @param - ID of the "About" section content.
   * @returns The updated "About" section content.
   */
  (
    _id: string,
    memberAbout: NonNullablePaths<UpdateMemberAbout, `content` | `revision`, 2>
  ): Promise<MemberAbout>;
}

export function deleteMemberAbout(
  httpClient: HttpClient
): DeleteMemberAboutSignature {
  return (_id: string) =>
    universalDeleteMemberAbout(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteMemberAboutSignature {
  /**
   * Deletes the "About" section content for a member.
   * @param - ID of the "About" section content to delete.
   */
  (_id: string): Promise<void>;
}

export function getMemberAbout(
  httpClient: HttpClient
): GetMemberAboutSignature {
  return (_id: string) =>
    universalGetMemberAbout(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface GetMemberAboutSignature {
  /**
   * Retrieves a member's "About" section content.
   * @param - ID of the "About" section content to retrieve.
   * @returns The retrieved "About" section content.
   */
  (_id: string): Promise<MemberAbout>;
}

export function getMyMemberAbout(
  httpClient: HttpClient
): GetMyMemberAboutSignature {
  return () =>
    universalGetMyMemberAbout(
      // @ts-ignore
      { httpClient }
    );
}

interface GetMyMemberAboutSignature {
  /**
   * Retrieves the "About" section content of the current member.
   *
   * >**Note:**
   * >This method requires [visitor or member authentication](https://dev.wix.com/docs/api-reference/articles/authentication/about-identities).
   */
  (): Promise<GetMyMemberAboutResponse>;
}

export function queryMemberAbouts(
  httpClient: HttpClient
): QueryMemberAboutsSignature {
  return () =>
    universalQueryMemberAbouts(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryMemberAboutsSignature {
  /**
   * Creates a query to retrieve a list of "About" sections.
   *
   * The `queryMemberAbouts()` method builds a query to retrieve a list of "About" sections and returns a [`MemberAboutsQueryBuilder`](https://dev.wix.com/docs/sdk/backend-modules/members/members-about/member-abouts-query-builder/eq) object.
   *
   * The returned object contains the query definition, which is typically used to run the query using the [`find()`](https://dev.wix.com/docs/sdk/backend-modules/members/members-about/member-abouts-query-builder/find) method.
   *
   * You can refine the query by chaining `MemberAboutsQueryBuilder` methods onto the query. `MemberAboutsQueryBuilder` methods enable you to sort, filter, and control the results `queryMemberAbouts()` returns.
   *
   * `queryMemberAbouts()` runs with these `MemberAboutsQueryBuilder` defaults, which you can override:
   *
   * - [`limit(50)`](https://dev.wix.com/docs/sdk/backend-modules/members/members-about/member-abouts-query-builder/limit)
   */
  (): MemberAboutsQueryBuilder;
}

export function typedQueryMemberAbouts(
  httpClient: HttpClient
): TypedQueryMemberAboutsSignature {
  return (query: MemberAboutQuery) =>
    universalTypedQueryMemberAbouts(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryMemberAboutsSignature {
  /** */
  (query: MemberAboutQuery): Promise<
    NonNullablePaths<QueryMemberAboutsResponse, `memberAbouts`, 2>
  >;
}

export const onMemberAboutCreated = EventDefinition(
  'wix.members.about.v2.member_about_created',
  true,
  (event: MemberAboutCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'entity.content.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.content.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.content.nodes.galleryData.items.video.thumbnail.duration',
            },
            { path: 'entity.content.nodes.galleryData.options.item.ratio' },
            { path: 'entity.content.nodes.imageData.image.duration' },
            { path: 'entity.content.nodes.mapData.mapSettings.lat' },
            { path: 'entity.content.nodes.mapData.mapSettings.lng' },
            { path: 'entity.content.nodes.pollData.poll.image.duration' },
            {
              path: 'entity.content.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.content.nodes.pollData.design.poll.background.image.duration',
            },
            { path: 'entity.content.nodes.appEmbedData.image.duration' },
            { path: 'entity.content.nodes.videoData.video.duration' },
            { path: 'entity.content.nodes.videoData.thumbnail.duration' },
            { path: 'entity.content.nodes.audioData.audio.duration' },
            { path: 'entity.content.nodes.audioData.coverImage.duration' },
            {
              path: 'entity.content.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.content.nodes.layoutData.backdropImage.media.duration',
            },
            { path: 'entity.content.nodes.shapeData.shape.duration' },
          ],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.content.metadata.createdTimestamp' },
            { path: 'entity.content.metadata.updatedTimestamp' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<MemberAboutCreatedEnvelope>();
export const onMemberAboutDeleted = EventDefinition(
  'wix.members.about.v2.member_about_deleted',
  true,
  (event: MemberAboutDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'undefined.content.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'undefined.content.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'undefined.content.nodes.galleryData.items.video.thumbnail.duration',
            },
            { path: 'undefined.content.nodes.galleryData.options.item.ratio' },
            { path: 'undefined.content.nodes.imageData.image.duration' },
            { path: 'undefined.content.nodes.mapData.mapSettings.lat' },
            { path: 'undefined.content.nodes.mapData.mapSettings.lng' },
            { path: 'undefined.content.nodes.pollData.poll.image.duration' },
            {
              path: 'undefined.content.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'undefined.content.nodes.pollData.design.poll.background.image.duration',
            },
            { path: 'undefined.content.nodes.appEmbedData.image.duration' },
            { path: 'undefined.content.nodes.videoData.video.duration' },
            { path: 'undefined.content.nodes.videoData.thumbnail.duration' },
            { path: 'undefined.content.nodes.audioData.audio.duration' },
            { path: 'undefined.content.nodes.audioData.coverImage.duration' },
            {
              path: 'undefined.content.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'undefined.content.nodes.layoutData.backdropImage.media.duration',
            },
            { path: 'undefined.content.nodes.shapeData.shape.duration' },
          ],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.content.metadata.createdTimestamp' },
            { path: 'undefined.content.metadata.updatedTimestamp' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<MemberAboutDeletedEnvelope>();
export const onMemberAboutUpdated = EventDefinition(
  'wix.members.about.v2.member_about_updated',
  true,
  (event: MemberAboutUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'entity.content.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.content.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.content.nodes.galleryData.items.video.thumbnail.duration',
            },
            { path: 'entity.content.nodes.galleryData.options.item.ratio' },
            { path: 'entity.content.nodes.imageData.image.duration' },
            { path: 'entity.content.nodes.mapData.mapSettings.lat' },
            { path: 'entity.content.nodes.mapData.mapSettings.lng' },
            { path: 'entity.content.nodes.pollData.poll.image.duration' },
            {
              path: 'entity.content.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.content.nodes.pollData.design.poll.background.image.duration',
            },
            { path: 'entity.content.nodes.appEmbedData.image.duration' },
            { path: 'entity.content.nodes.videoData.video.duration' },
            { path: 'entity.content.nodes.videoData.thumbnail.duration' },
            { path: 'entity.content.nodes.audioData.audio.duration' },
            { path: 'entity.content.nodes.audioData.coverImage.duration' },
            {
              path: 'entity.content.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.content.nodes.layoutData.backdropImage.media.duration',
            },
            { path: 'entity.content.nodes.shapeData.shape.duration' },
          ],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.content.metadata.createdTimestamp' },
            { path: 'entity.content.metadata.updatedTimestamp' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<MemberAboutUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  Alignment,
  AnchorData,
  AppEmbedData,
  AppEmbedDataAppDataOneOf,
  AppType,
  AspectRatio,
  AudioData,
  Background,
  BackgroundBackgroundOneOf,
  BackgroundImage,
  BackgroundType,
  Banner,
  BaseEventMetadata,
  BlockquoteData,
  BookingData,
  Border,
  BorderColors,
  BorderWidths,
  BulletedListData,
  ButtonData,
  ButtonDataType,
  ButtonStyles,
  CaptionData,
  CardStyles,
  CellStyle,
  CodeBlockData,
  CollapsibleListData,
  ColorData,
  Colors,
  CreateMemberAboutRequest,
  CreateMemberAboutResponse,
  Crop,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  Decoration,
  DecorationDataOneOf,
  DecorationType,
  DeleteMemberAboutRequest,
  DeleteMemberAboutResponse,
  Design,
  DesignTarget,
  Dimensions,
  Direction,
  DividerData,
  DividerDataAlignment,
  DocumentStyle,
  DomainEvent,
  DomainEventBodyOneOf,
  EmbedData,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventData,
  EventMetadata,
  FileData,
  FileSource,
  FileSourceDataOneOf,
  FontFamilyData,
  FontSizeData,
  FontType,
  GIF,
  GIFData,
  GIFType,
  GalleryData,
  GalleryOptions,
  GalleryOptionsLayout,
  GetMemberAboutRequest,
  GetMemberAboutResponse,
  GetMyMemberAboutRequest,
  GetMyMemberAboutResponse,
  Gradient,
  HTMLData,
  HTMLDataDataOneOf,
  HeadingData,
  Height,
  IdentificationData,
  IdentificationDataIdOneOf,
  Image,
  ImageData,
  ImageDataStyles,
  ImagePosition,
  ImageStyles,
  InitialExpandedItems,
  Item,
  ItemDataOneOf,
  ItemStyle,
  Layout,
  LayoutCellData,
  LayoutData,
  LayoutType,
  LineStyle,
  Link,
  LinkData,
  LinkDataOneOf,
  LinkPreviewData,
  LinkPreviewDataStyles,
  ListValue,
  MapData,
  MapSettings,
  MapType,
  Media,
  MemberAbout,
  MemberAboutCreatedEnvelope,
  MemberAboutDeletedEnvelope,
  MemberAboutQuerySpec,
  MemberAboutUpdatedEnvelope,
  MemberAboutsQueryBuilder,
  MemberAboutsQueryResult,
  MentionData,
  MessageEnvelope,
  Metadata,
  Node,
  NodeDataOneOf,
  NodeStyle,
  NodeType,
  NullValue,
  Oembed,
  Option,
  OptionDesign,
  OptionLayout,
  OrderedListData,
  Orientation,
  Origin,
  PDFSettings,
  ParagraphData,
  Permissions,
  Placement,
  PlaybackOptions,
  PluginContainerData,
  PluginContainerDataAlignment,
  PluginContainerDataWidth,
  PluginContainerDataWidthDataOneOf,
  Poll,
  PollData,
  PollDataLayout,
  PollDesign,
  PollLayout,
  PollLayoutDirection,
  PollLayoutType,
  Position,
  PricingData,
  QueryMemberAboutsRequest,
  QueryMemberAboutsResponse,
  Rel,
  Resizing,
  ResponsivenessBehaviour,
  RestoreInfo,
  RibbonStyles,
  RichContent,
  Scaling,
  Settings,
  ShapeData,
  ShapeDataStyles,
  SortOrder,
  Sorting,
  Source,
  Spoiler,
  SpoilerData,
  Styles,
  StylesBorder,
  StylesPosition,
  TableCellData,
  TableData,
  Target,
  TextAlignment,
  TextData,
  TextNodeStyle,
  TextStyle,
  Thumbnails,
  ThumbnailsAlignment,
  Type,
  UpdateMemberAbout,
  UpdateMemberAboutRequest,
  UpdateMemberAboutResponse,
  VerticalAlignment,
  VerticalAlignmentAlignment,
  Video,
  VideoData,
  ViewMode,
  ViewRole,
  VoteRole,
  WebhookIdentityType,
  Width,
  WidthType,
  utils,
} from './members-about-v2-member-about-members-about.universal.js';
